import { Pause, Play } from "lucide-react";
import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { useAlbumTracks } from "@web/features/library/api.ts";
import { queueTrackFromRow } from "@web/features/player/types.ts";
import { usePlayerStore } from "@web/features/player/playerStore.ts";
import { formatDuration } from "@web/lib/format.ts";
import { cn } from "@web/lib/utils.ts";

/** Album sheet — library tracks on this release. */
export function AlbumTracksSection({ albumId }: { albumId: string }) {
  const tracks = useAlbumTracks(albumId);
  const openDetail = useOpenDetail();
  const currentId = usePlayerStore((s) =>
    s.queueIndex >= 0 ? (s.queue[s.queueIndex]?.id ?? null) : null,
  );
  const playing = usePlayerStore((s) => s.playing);

  if (tracks.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} className="h-11 w-full" />
        ))}
      </div>
    );
  }

  if (tracks.isError) {
    return (
      <EmptyState
        title="Couldn't load tracks"
        description={tracks.error instanceof Error ? tracks.error.message : "Unknown error"}
      />
    );
  }

  const list = tracks.data ?? [];
  if (list.length === 0) {
    return (
      <EmptyState
        title="No tracks in your library"
        description="No mirrored tracks point at this album yet."
      />
    );
  }

  const queue = list.map(queueTrackFromRow).filter((t): t is NonNullable<typeof t> => t !== null);

  function playFrom(trackId: string) {
    if (currentId === trackId) {
      void usePlayerStore.getState().togglePlay();
      return;
    }
    void usePlayerStore.getState().playTracks(queue, trackId);
  }

  return (
    <ul className="divide-y divide-line">
      {list.map((track) => {
        const artists = track.artists.map((a) => a.name).filter(Boolean).join(", ");
        const isCurrent = currentId === track.id;
        const isThisPlaying = isCurrent && playing;
        const num =
          track.track_number != null ? String(track.track_number).padStart(2, "0") : "—";

        return (
          <li key={track.id}>
            <div className="group/row flex w-full items-center gap-3 px-1 py-2 hover:bg-row-hover">
              <span className="w-6 shrink-0 text-center font-mono text-xs text-faint tabular-nums">
                {num}
              </span>
              <div className="relative size-9 shrink-0">
                <EntityAvatar
                  src={track.cover_url ?? track.album?.cover_url ?? null}
                  alt=""
                  size="sm"
                />
                <button
                  type="button"
                  className={cn(
                    "absolute inset-0 flex items-center justify-center rounded-sm bg-black/60 text-text",
                    "opacity-0 transition-opacity group-hover/row:opacity-100",
                    "focus-visible:opacity-100 focus-visible:outline-none",
                    isThisPlaying && "opacity-100",
                  )}
                  aria-label={isThisPlaying ? `Pause ${track.name}` : `Play ${track.name}`}
                  onClick={() => playFrom(track.id)}
                >
                  {isThisPlaying ? (
                    <Pause className="size-3.5 fill-current" />
                  ) : (
                    <Play className="size-3.5 fill-current" />
                  )}
                </button>
              </div>
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() =>
                  openDetail(
                    { type: "track", id: track.id, label: track.name },
                    { mode: "push" },
                  )
                }
              >
                <span
                  className={cn(
                    "block truncate text-sm font-medium",
                    isCurrent && "text-accent",
                  )}
                >
                  {track.name}
                </span>
                <span className="block truncate text-xs text-faint">{artists || "—"}</span>
              </button>
              <span className="shrink-0 font-mono text-xs text-faint tabular-nums">
                {formatDuration(track.duration_ms)}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
